import { invariantResponse } from '@epic-web/invariant'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { motion } from 'framer-motion'
import { type LoaderFunctionArgs } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { BlockRenderer } from '#app/components/block-renderer.tsx'
import { ViewTransitionLink } from '#app/components/view-transition-link.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { buildMeta, buildServiceJsonLd, buildBreadcrumbJsonLd } from '#app/utils/meta.ts'
import { type Route } from './+types/_marketing.diensten.$slug.ts'

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => {
		const services = await prisma.service.findMany({
			select: { slug: true },
		})
		return services.map((service) => ({
			route: `/diensten/${service.slug}`,
			priority: 0.7,
		}))
	}),
}

export const meta: Route.MetaFunction = ({ data }) => {
	if (!data?.service) {
		return [{ title: 'Dienst niet gevonden' }]
	}
	const page = data.service.page
	const title = page?.metaTitle || `${data.service.name}`
	const description = page?.metaDescription || data.service.description || `Ontdek onze ${data.service.name} diensten bij Ignite Identity.`
	return [
		...buildMeta({
			title,
			description,
			url: `/diensten/${data.service.slug}`,
		}),
		buildServiceJsonLd({
			name: data.service.name,
			description,
			url: `/diensten/${data.service.slug}`,
		}),
		buildBreadcrumbJsonLd([
			{ name: 'Home', url: '/' },
			{ name: 'Diensten', url: '/diensten' },
			{ name: data.service.name, url: `/diensten/${data.service.slug}` },
		]),
	]
}

export async function loader({ params }: LoaderFunctionArgs) {
	invariantResponse(params.slug, 'Slug is required')

	const service = await prisma.service.findUnique({
		where: { slug: params.slug },
		include: {
			page: {
				include: {
					blocks: {
						orderBy: { order: 'asc' },
					},
				},
			},
			caseStudies: {
				where: { published: true },
				select: {
					id: true,
					title: true,
					slug: true,
					client: true,
					imageUrl: true,
				},
				orderBy: { order: 'asc' },
				take: 6,
			},
		},
	})

	invariantResponse(service, 'Dienst niet gevonden', { status: 404 })

	// Check if page is published (or show anyway for preview)
	// For now, always show the page if it exists
	if (!service.page) {
		// Return service without page - will show fallback
		const services = await prisma.service.findMany({
			select: { id: true, name: true, slug: true, description: true },
			orderBy: { name: 'asc' },
		})
		return { service, services, caseStudies: service.caseStudies, newsArticles: [] }
	}

	// Fetch additional data for blocks
	const [services, allCaseStudies, newsArticles] = await Promise.all([
		prisma.service.findMany({
			select: { id: true, name: true, slug: true, description: true },
			orderBy: { name: 'asc' },
		}),
		prisma.caseStudy.findMany({
			where: { published: true },
			select: {
				id: true,
				title: true,
				slug: true,
				client: true,
				imageUrl: true,
			},
			orderBy: { order: 'asc' },
		}),
		prisma.newsArticle.findMany({
			where: { published: true },
			select: {
				id: true,
				title: true,
				slug: true,
				excerpt: true,
				featuredImage: true,
				category: true,
				publishedAt: true,
			},
			orderBy: { publishedAt: 'desc' },
			take: 10,
		}),
	])

	// Map news articles for block renderer
	const mappedNewsArticles = newsArticles.map((a) => ({
		id: a.id,
		title: a.title,
		slug: a.slug,
		excerpt: a.excerpt,
		imageUrl: a.featuredImage,
		category: a.category,
		publishedAt: a.publishedAt?.toISOString() || null,
	}))

	return { service, services, caseStudies: allCaseStudies, newsArticles: mappedNewsArticles }
}

// Animation variants
const fadeIn = {
	initial: { opacity: 0, y: 12 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
}

export default function ServicePage({ loaderData }: Route.ComponentProps) {
	const { service, services, caseStudies, newsArticles } = loaderData

	const hasBlocks = service.page?.blocks && service.page.blocks.length > 0

	// If there are blocks, render them with BlockRenderer
	if (hasBlocks && service.page) {
		return (
			<main>
				<BlockRenderer
					blocks={service.page.blocks}
					services={services}
					caseStudies={caseStudies}
					newsArticles={newsArticles}
				/>
			</main>
		)
	}

	// Fallback: simple service page layout
	return (
		<motion.main
			className="bg-white"
			initial="initial"
			animate="animate"
		>
			{/* Hero Section */}
			<section className="relative bg-[#1a1a1a] pt-32 pb-16 md:pb-24">
				{/* Breadcrumb Navigation */}
				<motion.nav
					className="relative z-10 px-6 py-4 md:px-12 lg:px-24 max-w-7xl mx-auto"
					variants={fadeIn}
				>
					<div className="flex items-center gap-2 text-sm text-white/60">
						<ViewTransitionLink to="/" className="hover:text-white transition-colors">
							Home
						</ViewTransitionLink>
						<span>/</span>
						<ViewTransitionLink to="/diensten" className="hover:text-white transition-colors">
							Diensten
						</ViewTransitionLink>
						<span>/</span>
						<span className="text-white">{service.name}</span>
					</div>
				</motion.nav>

				<div className="relative z-10 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">
					<motion.h1
						className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6"
						variants={fadeIn}
					>
						{service.name}
					</motion.h1>
					{service.description && (
						<motion.p
							className="text-lg md:text-xl text-white/70 max-w-2xl"
							variants={fadeIn}
						>
							{service.description}
						</motion.p>
					)}
				</div>
			</section>

			{/* Related Case Studies */}
			{service.caseStudies && service.caseStudies.length > 0 && (
				<section className="px-6 py-16 md:px-12 md:py-24 lg:px-24">
					<div className="max-w-7xl mx-auto">
						<motion.h2
							className="text-2xl md:text-3xl font-bold mb-8"
							variants={fadeIn}
						>
							Gerelateerde cases
						</motion.h2>
						<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
							{service.caseStudies.map((caseStudy) => (
								<ViewTransitionLink
									key={caseStudy.id}
									to={`/cases/${caseStudy.slug}`}
									className="group block"
								>
									<div className="relative aspect-[4/3] rounded-lg overflow-hidden mb-4 bg-gray-200">
										{caseStudy.imageUrl && (
											<img
												src={caseStudy.imageUrl}
												alt={caseStudy.title}
												className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
											/>
										)}
									</div>
									<h3 className="text-lg font-semibold group-hover:text-primary transition-colors">
										{caseStudy.title}
									</h3>
									{caseStudy.client && (
										<p className="text-sm text-muted-foreground">{caseStudy.client}</p>
									)}
								</ViewTransitionLink>
							))}
						</div>
					</div>
				</section>
			)}
		</motion.main>
	)
}
